import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroArrowsPointingOut, heroPhoto, heroStar, heroTrash } from '@ng-icons/heroicons/outline';
import { heroStarSolid } from '@ng-icons/heroicons/solid';
import { FilesApiService } from '../../../core/api/files-api.service';
import {
  ToastrService,
  UiButton,
  UiConfirmDialogService,
  UiFilePicker,
  UiSpinner,
} from '../../../shared/ui';
import type { RevenueProductImage } from '../../../core/api/generated/models/RevenueProductImage';
import { RevenueApiService } from './revenue-api.service';
import { PRODUCT_IMAGE_MAX, uploadProductImage, validateProductImage } from './product-image-upload';

/**
 * Product image gallery manager: upload (drag/drop or picker), reorder (drag),
 * set cover, remove. Reused on the product detail page. Communicates limits and
 * behavior in-line (max 8, cover auto-promotes on remove).
 */
@Component({
  selector: 'app-product-gallery',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkDropList, CdkDrag, NgIcon, UiButton, UiFilePicker, UiSpinner],
  providers: [provideIcons({ heroArrowsPointingOut, heroPhoto, heroStar, heroStarSolid, heroTrash })],
  template: `
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <p class="text-sm text-muted">
          {{ images().length }}/{{ max }} imagens
          @if (images().length > 0) {
            <span class="text-xs">· arraste para reordenar · a primeira é a capa</span>
          }
        </p>
        @if (uploading()) {
          <app-ui-spinner />
        }
      </div>

      @if (loading()) {
        <div class="grid grid-cols-3 gap-3 sm:grid-cols-4">
          @for (i of [1, 2, 3, 4]; track i) {
            <div class="aspect-square animate-pulse rounded-lg bg-soft"></div>
          }
        </div>
      } @else {
        <div
          cdkDropList
          cdkDropListOrientation="mixed"
          class="grid grid-cols-3 gap-3 sm:grid-cols-4"
          (cdkDropListDropped)="onDrop($event)"
        >
          @for (image of images(); track image.id) {
            <figure
              cdkDrag
              class="group relative aspect-square overflow-hidden rounded-lg border border-line bg-soft"
            >
              <img
                [src]="image.url"
                [alt]="'Imagem do produto'"
                class="h-full w-full object-cover"
                loading="lazy"
              />
              @if (image.isCover) {
                <span class="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-panel">
                  <ng-icon name="heroStarSolid" size="11" /> Capa
                </span>
              }
              <div
                class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/70 p-2 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100"
              >
                @if (!image.isCover) {
                  <button appButton variant="secondary" size="sm" type="button" (click)="setCover(image)">
                    <ng-icon name="heroStar" size="15" /> Definir capa
                  </button>
                }
                <button appButton variant="danger" size="sm" type="button" (click)="remove(image)">
                  <ng-icon name="heroTrash" size="15" /> Remover
                </button>
              </div>
            </figure>
          }
        </div>

        @if (images().length < max) {
          <app-ui-file-picker
            variant="dropzone"
            icon="heroPhoto"
            placeholder="Arraste imagens aqui ou clique para enviar"
            hint="JPEG, PNG, WEBP ou GIF · até 2 MB cada"
            accept="image/jpeg,image/png,image/webp,image/gif"
            [multiple]="true"
            (fileChange)="onFilesPicked($event)"
          />
        } @else {
          <p class="text-xs text-muted">Limite de {{ max }} imagens atingido. Remova uma para adicionar outra.</p>
        }
      }
    </div>
  `,
})
export class ProductGalleryComponent {
  private readonly api = inject(RevenueApiService);
  private readonly files = inject(FilesApiService);
  private readonly toastr = inject(ToastrService);
  private readonly confirm = inject(UiConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);

  readonly productId = input.required<string>();

  readonly max = PRODUCT_IMAGE_MAX;
  readonly images = signal<RevenueProductImage[]>([]);
  readonly loading = signal(true);
  readonly uploading = signal(false);

  constructor() {
    effect(() => {
      const id = this.productId();
      if (id) this.load(id);
    });
  }

  private load(id: string): void {
    this.loading.set(true);
    this.api
      .listImages(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.images.set(resp.data);
          this.loading.set(false);
        },
        error: () => {
          this.toastr.error('Não foi possível carregar as imagens.');
          this.loading.set(false);
        },
      });
  }

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const list = input?.files;
    if (!list || list.length === 0) return;
    const remaining = this.max - this.images().length;
    const files = Array.from(list).slice(0, remaining);
    if (list.length > remaining) {
      this.toastr.info(`Só é possível adicionar ${remaining} imagem(ns) — o limite é ${this.max}.`);
    }
    for (const file of files) this.upload(file);
    if (input) input.value = '';
  }

  private upload(file: File): void {
    const error = validateProductImage(file);
    if (error) {
      this.toastr.error(error);
      return;
    }
    this.uploading.set(true);
    uploadProductImage(this.files, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fileId) => this.attach(fileId),
        error: () => {
          this.toastr.error('Falha no upload da imagem.');
          this.uploading.set(false);
        },
      });
  }

  private attach(fileId: string): void {
    this.api
      .addImage(this.productId(), fileId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (image) => {
          this.images.update((list) => [...list, image]);
          this.uploading.set(false);
        },
        error: (err) => {
          this.toastr.error('Não foi possível anexar a imagem.', err?.error?.message);
          this.uploading.set(false);
        },
      });
  }

  setCover(image: RevenueProductImage): void {
    this.api
      .setImageCover(this.productId(), image.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => this.images.set(resp.data),
        error: () => this.toastr.error('Não foi possível definir a capa.'),
      });
  }

  async remove(image: RevenueProductImage): Promise<void> {
    const ok = await this.confirm.confirm({
      title: 'Remover imagem?',
      description: image.isCover
        ? 'Esta é a capa. Ao remover, a próxima imagem vira a capa automaticamente.'
        : 'A imagem será removida da galeria.',
      confirmLabel: 'Remover',
      tone: 'danger',
    });
    if (!ok) return;
    this.api
      .removeImage(this.productId(), image.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.load(this.productId()),
        error: () => this.toastr.error('Não foi possível remover a imagem.'),
      });
  }

  onDrop(event: CdkDragDrop<RevenueProductImage[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const next = [...this.images()];
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    this.images.set(next);
    this.api
      .reorderImages(this.productId(), next.map((i) => i.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => this.images.set(resp.data),
        error: () => {
          this.toastr.error('Não foi possível reordenar.');
          this.load(this.productId());
        },
      });
  }
}
